import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, 
  Link as LinkIcon, 
  Upload, 
  Loader, 
  CheckCircle, 
  AlertCircle,
  BookOpen,
  TrendingUp,
  DollarSign,
  BarChart3
} from 'lucide-react';

interface AnalysisResult {
  summary: string;
  keyConcepts: Array<{
    term: string;
    definition: string;
    importance: string;
  }>;
  economicIndicators: string[];
  recommendations: string[];
  complexity: 'Beginner' | 'Intermediate' | 'Advanced';
}

const ArticleAnalyzer: React.FC = () => {
  const [inputType, setInputType] = useState<'url' | 'upload'>('url');
  const [url, setUrl] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      setError(null);
    } else {
      setError('Please upload a PDF file only.');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const mockAnalysisResult: AnalysisResult = {
    summary: "This article discusses the impact of monetary policy on inflation control, covering the Federal Reserve's recent interest rate decisions and their implications for economic growth. The analysis reveals key insights into the trade-offs between inflation control and employment levels.",
    keyConcepts: [
      {
        term: "Monetary Policy",
        definition: "The process by which a central bank controls the money supply and interest rates to influence economic activity.",
        importance: "Central to understanding how governments manage economic stability and growth."
      },
      {
        term: "Federal Funds Rate",
        definition: "The interest rate at which banks lend money to each other overnight, set by the Federal Reserve.",
        importance: "This rate influences all other interest rates in the economy and affects borrowing costs."
      },
      {
        term: "Phillips Curve",
        definition: "An economic concept showing the inverse relationship between unemployment and inflation rates.",
        importance: "Helps explain the trade-off between employment and price stability in economic policy."
      },
      {
        term: "Quantitative Easing",
        definition: "A monetary policy tool where central banks purchase government securities to increase money supply.",
        importance: "Used during economic downturns to stimulate economic activity when traditional methods are insufficient."
      }
    ],
    economicIndicators: [
      "Consumer Price Index (CPI)",
      "Core Inflation Rate",
      "Unemployment Rate",
      "GDP Growth Rate",
      "Federal Funds Rate"
    ],
    recommendations: [
      "Study the relationship between interest rates and inflation",
      "Monitor Federal Reserve meeting minutes for policy insights",
      "Understand the dual mandate of the Federal Reserve",
      "Learn about the transmission mechanism of monetary policy"
    ],
    complexity: "Intermediate"
  };

  const analyzeContent = async () => {
    if (!url && !uploadedFile) {
      setError('Please provide a URL or upload a PDF file.');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 3000));
      setAnalysisResult(mockAnalysisResult);
    } catch (err) {
      setError('Failed to analyze the content. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const reset = () => {
    setUrl('');
    setUploadedFile(null);
    setAnalysisResult(null);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Economics Article Analyzer</h1>
        <p className="mt-2 text-gray-600">
          Understand complex economic concepts from articles and documents with AI-powered analysis
        </p>
      </div>

      {/* Input Section */}
      <div className="card">
        <div className="flex items-center justify-center mb-6">
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setInputType('url')}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                inputType === 'url'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <LinkIcon className="h-4 w-4 mr-2" />
              Article URL
            </button>
            <button
              onClick={() => setInputType('upload')}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                inputType === 'upload'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload PDF
            </button>
          </div>
        </div>

        {inputType === 'url' ? (
          <div className="space-y-4">
            <div>
              <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                Article URL
              </label>
              <input
                type="url"
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/economics-article"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                {isDragActive
                  ? 'Drop the PDF file here...'
                  : 'Drag & drop a PDF file here, or click to select'}
              </p>
              <p className="text-xs text-gray-500 mt-1">PDF files only, max 10MB</p>
            </div>
            {uploadedFile && (
              <div className="flex items-center p-3 bg-green-50 rounded-lg">
                <FileText className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm text-green-800">{uploadedFile.name}</span>
                <CheckCircle className="h-5 w-5 text-green-600 ml-auto" />
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="flex items-center p-3 bg-red-50 rounded-lg mt-4">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        )}

        <div className="flex justify-center mt-6 space-x-4">
          <button
            onClick={analyzeContent}
            disabled={isAnalyzing || (!url && !uploadedFile)}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <BookOpen className="h-4 w-4 mr-2" />
                Analyze Content
              </>
            )}
          </button>
          {(url || uploadedFile || analysisResult) && (
            <button onClick={reset} className="btn-secondary">
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Analysis Results */}
      {analysisResult && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="card">
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Summary</h2>
              <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                analysisResult.complexity === 'Beginner' ? 'bg-green-100 text-green-800' :
                analysisResult.complexity === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {analysisResult.complexity} Level
              </span>
            </div>
            <p className="text-gray-700 leading-relaxed">{analysisResult.summary}</p>
          </div>

          {/* Key Economic Concepts */}
          <div className="card">
            <div className="flex items-center mb-4">
              <BookOpen className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Key Economic Concepts</h2>
            </div>
            <div className="space-y-4">
              {analysisResult.keyConcepts.map((concept, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{concept.term}</h3>
                  <p className="text-gray-700 mb-2">{concept.definition}</p>
                  <div className="flex items-start">
                    <TrendingUp className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-700">{concept.importance}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Economic Indicators */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="flex items-center mb-4">
                <BarChart3 className="h-6 w-6 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Related Economic Indicators</h2>
              </div>
              <ul className="space-y-2">
                {analysisResult.economicIndicators.map((indicator, index) => (
                  <li key={index} className="flex items-center">
                    <DollarSign className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-gray-700">{indicator}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Learning Recommendations */}
            <div className="card">
              <div className="flex items-center mb-4">
                <BookOpen className="h-6 w-6 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Learning Recommendations</h2>
              </div>
              <ul className="space-y-2">
                {analysisResult.recommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleAnalyzer; 